import { useState, useCallback, useRef } from 'react';
import { Model } from '../../types';
import { ExportManager, ExportConfig, ExportResult, ExportFormat } from './ExportManager';
import { ExportQuality } from './exportTypes';

export interface UseExportOptions {
    onSuccess?: (result: ExportResult) => void;
    onError?: (error: Error) => void;
    onProgress?: (progress: number) => void;
}

/**
 * React hook for exporting diagrams
 */
export const useExport = (options: UseExportOptions = {}) => {
    const [isExporting, setIsExporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [lastResult, setLastResult] = useState<ExportResult | null>(null);
    const exportManagerRef = useRef(new ExportManager());

    const updateProgress = useCallback(
        (value: number) => {
            setProgress(value);
            options.onProgress?.(value);
        },
        [options]
    );

    /**
     * Export diagram
     */
    const exportDiagram = useCallback(
        async (
            element: HTMLElement,
            model: Model,
            config: Partial<ExportConfig> = {}
        ): Promise<ExportResult> => {
            setIsExporting(true);
            updateProgress(0);

            try {
                updateProgress(25);
                const result = await exportManagerRef.current.export(element, model, config);
                updateProgress(100);

                setLastResult(result);

                if (result.success) {
                    options.onSuccess?.(result);
                } else if (result.error) {
                    options.onError?.(result.error);
                }

                return result;
            } catch (error) {
                const errorResult: ExportResult = {
                    success: false,
                    error: error as Error,
                    filename: '',
                };
                setLastResult(errorResult);
                options.onError?.(error as Error);
                return errorResult;
            } finally {
                setIsExporting(false);
                setTimeout(() => setProgress(0), 1000);
            }
        },
        [options, updateProgress]
    );

    /**
     * Export and download immediately
     */
    const exportAndDownload = useCallback(
        async (
            element: HTMLElement,
            model: Model,
            config: Partial<ExportConfig> = {}
        ): Promise<ExportResult> => {
            setIsExporting(true);
            updateProgress(0);

            try {
                updateProgress(25);
                const result = await exportManagerRef.current.exportAndDownload(
                    element,
                    model,
                    config
                );
                updateProgress(100);

                setLastResult(result);

                if (result.success) {
                    options.onSuccess?.(result);
                } else if (result.error) {
                    options.onError?.(result.error);
                }

                return result;
            } catch (error) {
                const errorResult: ExportResult = {
                    success: false,
                    error: error as Error,
                    filename: '',
                };
                setLastResult(errorResult);
                options.onError?.(error as Error);
                return errorResult;
            } finally {
                setIsExporting(false);
                setTimeout(() => setProgress(0), 1000);
            }
        },
        [options, updateProgress]
    );

    /**
     * Download a previous export result
     */
    const download = useCallback(
        async (result: ExportResult): Promise<void> => {
            try {
                await exportManagerRef.current.download(result);
            } catch (error) {
                options.onError?.(error as Error);
            }
        },
        [options]
    );

    /**
     * Export to PNG
     * FIXED: quality parameter now typed as ExportQuality (1 | 2 | 4)
     */
    const exportToPNG = useCallback(
        async (
            element: HTMLElement,
            model: Model,
            quality: ExportQuality = 2,
            transparent: boolean = false
        ) => {
            return exportAndDownload(element, model, {
                format: 'png',
                quality,
                transparent,
            });
        },
        [exportAndDownload]
    );

    /**
     * Export to JPEG
     * FIXED: quality parameter now typed as ExportQuality (1 | 2 | 4)
     */
    const exportToJPEG = useCallback(
        async (
            element: HTMLElement,
            model: Model,
            quality: ExportQuality = 2,
            backgroundColor: string = '#ffffff'
        ) => {
            return exportAndDownload(element, model, {
                format: 'jpeg',
                quality,
                backgroundColor,
            });
        },
        [exportAndDownload]
    );

    /**
     * Export to SVG
     */
    const exportToSVG = useCallback(
        async (
            element: HTMLElement,
            model: Model,
            transparent: boolean = false
        ) => {
            return exportAndDownload(element, model, {
                format: 'svg',
                transparent,
            });
        },
        [exportAndDownload]
    );

    /**
     * Export to JSON
     */
    const exportToJSON = useCallback(
        async (element: HTMLElement, model: Model) => {
            return exportAndDownload(element, model, {
                format: 'json',
            });
        },
        [exportAndDownload]
    );

    /**
     * Get supported formats
     */
    const getSupportedFormats = useCallback((): ExportFormat[] => {
        return exportManagerRef.current.getSupportedFormats();
    }, []);

    /**
     * Get default config
     */
    const getDefaultConfig = useCallback((): ExportConfig => {
        return exportManagerRef.current.getDefaultConfig();
    }, []);

    /**
     * Update default config
     */
    const updateDefaultConfig = useCallback((config: Partial<ExportConfig>) => {
        exportManagerRef.current.updateDefaultConfig(config);
    }, []);

    /**
     * Validate config
     */
    const validateConfig = useCallback((config: Partial<ExportConfig>): boolean => {
        return exportManagerRef.current.validateConfig(config);
    }, []);

    return {
        // State
        isExporting,
        progress,
        lastResult,

        // Core methods
        exportDiagram,
        exportAndDownload,
        download,

        // Convenience methods
        exportToPNG,
        exportToJPEG,
        exportToSVG,
        exportToJSON,

        // Config
        getSupportedFormats,
        getDefaultConfig,
        updateDefaultConfig,
        validateConfig,

        // Manager instance
        exportManager: exportManagerRef.current,
    };
};